import React, { useMemo, useState } from "react";
import SignaturePad, { isSignatureDataUrl } from "@/components/SignaturePad";

const FONT = "'Nohemi', 'Space Grotesk', sans-serif";
const TEXT = "#222222";
const ACCENT = "#001EFF";
const META = "#9E9B93";
const RULE = "#E8E4DC";
const BG = "#FCFCFC";

/** Fixed legal frame — mirrors backend/barter_agreement.py LEGAL_FRAME. */
export const BARTER_LEGAL_FRAME = `The Offering — TAĪSTU provides {{PARTNER}} with a partnership that moves the business forward with them. It lives in the space between design, web and product design, operational systems, and hands-on execution — done for you, and always pointed toward wherever the business needs it most. A fresh scope frames each month's focus, while a shared portal holds the work, progress, and communication in one place. A month's scope may vary between one to three focus areas, aligned with what the business needs.

The exchange is systems-based — TAĪSTU designs and builds the structures, workflows, and infrastructure that bring ease to the founder, rather than executing one-off requests. The aim is always to leave the business more organized, more capable, and more its own.

Valued at {{TAISTU_VALUE}}.

Terms of Services — This is a month-to-month, at-will partnership, and either party may end it at any time. Because we're sharing our gifts equally, the exchange stays balanced on the way out: whatever share of value one party has given, the other gives the same share in return.

Pausing Exchange — If either party can't deliver in a given month, that month's exchange pauses — (no delivery from one side → no obligation from the other). Nothing is owed for a paused month, and the partnership may simply resume the next month either party is ready.

Ownership + Handoff — Every system, design, and build TAĪSTU delivers is {{PARTNER}}'s to own, outright. Systems, designs, and any work produced in the course of this partnership are owned fully by {{PARTNER}} and handed off in full — no retained ownership, no dependency on TAĪSTU to keep it running. What we build, you keep. Any pre-existing tools, frameworks, or methods TAĪSTU uses to build remain TAĪSTU's own.

Revisions — This agreement is meant to flex as the partnership grows. Either party may propose changes at any time, and any revision takes effect once it's agreed in writing and attached here. The intention is a partnership held with integrity and flexibility — open to conversation and adjusted as needs evolve.`;

export const MOCK_BARTER_AGREEMENT_FIELDS = {
  FOUNDER: "Maya Chen",
  PARTNER: "Lumen & Clay",
  START_DATE: "2026-08-01",
  TAISTU_PROVIDES: "Operational Ecosystem",
  TAISTU_VALUE: "$2,500",
  PARTNER_PROVIDES: "one studio photoshoot day per quarter and ongoing product credit",
  PARTNER_VALUE: "$2,500",
};

/** Editable merge fields for the admin drawer (legal frame stays fixed). */
export const BARTER_EDITABLE_FIELDS = [
  { key: "FOUNDER", label: "Founder", placeholder: "Contact name" },
  { key: "PARTNER", label: "Partner", placeholder: "Company name" },
  { key: "START_DATE", label: "Start date", placeholder: "Select date", type: "date" },
  { key: "TAISTU_PROVIDES", label: "Offering", placeholder: "Operational Ecosystem" },
  { key: "TAISTU_VALUE", label: "Value", placeholder: "$2,500" },
  { key: "PARTNER_PROVIDES", label: "Partner provides", placeholder: "photoshoot day…", multiline: true },
  { key: "PARTNER_VALUE", label: "Partner value", placeholder: "$2,500" },
];

/** Normalize start date to YYYY-MM-DD when possible (keeps free-text otherwise). */
export function toBarterStartDateIso(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
  const parsed = Date.parse(raw);
  if (Number.isNaN(parsed)) return raw;
  const d = new Date(parsed);
  if (Number.isNaN(d.getTime())) return raw;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** Display start date in agreement copy (e.g. August 1, 2026). */
export function formatBarterStartDate(value) {
  const iso = toBarterStartDateIso(value);
  if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) {
    const [y, m, d] = iso.split("-").map(Number);
    const dt = new Date(y, m - 1, d);
    return dt.toLocaleDateString(undefined, {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  }
  return String(value || "").trim();
}

/** Seed empty founder/partner from the prospect record. */
export function seedBarterFieldsFromClient(fields, client) {
  const base = normalizeBarterFields(fields || {});
  return normalizeBarterFields({
    ...base,
    FOUNDER: base.FOUNDER || client?.primary_contact_name || "",
    PARTNER: base.PARTNER || client?.name || "",
    START_DATE: toBarterStartDateIso(base.START_DATE) || base.START_DATE || "",
  });
}

export function barterFieldsAreFilled(fields) {
  const f = normalizeBarterFields(fields || {});
  return Boolean(
    f.PARTNER &&
      (f.TAISTU_PROVIDES || f.PARTNER_PROVIDES || f.TAISTU_VALUE || f.PARTNER_VALUE || f.START_DATE),
  );
}

export function barterAgreementFileName(fieldsOrName) {
  const partner =
    typeof fieldsOrName === "string"
      ? fieldsOrName
      : fieldsOrName?.PARTNER || fieldsOrName?.partner || "Partner";
  return `${String(partner || "Partner").trim() || "Partner"} – In-Kind Partnership Agreement.pdf`;
}

export function barterAgreementStatusLabel(status) {
  switch (String(status || "").toLowerCase()) {
    case "signed":
      return "Signed";
    case "partner_signed":
      return "Awaiting TAĪSTU signature";
    case "pending":
      return "Awaiting partner signature";
    case "draft":
    default:
      return "Not shared yet";
  }
}

export function normalizeBarterFields(raw = {}) {
  const out = {
    FOUNDER: String(raw.FOUNDER || raw.PARTNER_SIGNATORY || "").trim(),
    PARTNER: String(raw.PARTNER || raw.PARTNER_ENTITY || "").trim(),
    START_DATE: toBarterStartDateIso(raw.START_DATE) || String(raw.START_DATE || "").trim(),
    TAISTU_PROVIDES: String(raw.TAISTU_PROVIDES || "").trim(),
    TAISTU_VALUE: String(raw.TAISTU_VALUE || "").trim(),
    PARTNER_PROVIDES: String(raw.PARTNER_PROVIDES || "").trim(),
    PARTNER_VALUE: String(raw.PARTNER_VALUE || "").trim(),
  };
  return out;
}

export function applyBarterMerge(template, fields) {
  const merged = normalizeBarterFields(fields);
  return String(template || "").replace(/\{\{\s*([A-Z_]+)\s*\}\}/g, (_, key) => merged[key] || "");
}

export function barterClausesFromFields(fields) {
  const text = applyBarterMerge(BARTER_LEGAL_FRAME, fields).trim();
  const blocks = text.split(
    /\n\n+(?=(?:The Offering|Terms of Services|Pausing Exchange|Ownership \+ Handoff|Revisions) — )/,
  );
  return blocks
    .map((block) => block.trim())
    .filter(Boolean)
    .map((block) => {
      const idx = block.indexOf(" — ");
      if (idx === -1) return { title: "Clause", body: block };
      return { title: block.slice(0, idx).trim(), body: block.slice(idx + 3).trim() };
    });
}

function ClauseBody({ body }) {
  const paragraphs = String(body || "")
    .split(/\n\n+/)
    .map((p) => p.trim())
    .filter(Boolean);
  return (
    <div>
      {paragraphs.map((para, i) => {
        const isValue = /^Valued at\b/i.test(para);
        return (
          <p
            key={`${i}-${para.slice(0, 24)}`}
            style={{
              fontSize: 13,
              fontWeight: 300,
              lineHeight: 1.65,
              margin: i === 0 ? 0 : "12px 0 0",
              color: isValue ? ACCENT : undefined,
              whiteSpace: "pre-line",
            }}
          >
            {para}
          </p>
        );
      })}
    </div>
  );
}

function formatStamp(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function SignatureSlot({
  partyLabel,
  signatoryLabel,
  image,
  signedAt,
  empty = false,
}) {
  const stamp = formatStamp(signedAt);
  return (
    <div className="min-w-0 flex-1" data-testid={`barter-sig-slot-${partyLabel.toLowerCase()}`}>
      <div
        style={{
          fontSize: 10,
          letterSpacing: "0.12em",
          textTransform: "uppercase",
          color: META,
          marginBottom: 8,
        }}
      >
        {partyLabel}
      </div>
      <div
        style={{
          minHeight: 72,
          borderBottom: `0.5px solid ${RULE}`,
          display: "flex",
          alignItems: "flex-end",
          paddingBottom: 8,
        }}
      >
        {image && isSignatureDataUrl(image) ? (
          <img
            src={image}
            alt={`${partyLabel} signature`}
            style={{ maxHeight: 64, maxWidth: "100%", objectFit: "contain" }}
            data-testid={`barter-sig-image-${partyLabel.toLowerCase()}`}
          />
        ) : empty ? null : (
          <span style={{ fontSize: 13, color: META, fontWeight: 300 }}>{signatoryLabel}</span>
        )}
      </div>
      <p
        style={{
          fontSize: 12,
          color: stamp ? ACCENT : META,
          margin: "8px 0 0",
          minHeight: "1.25rem",
        }}
        data-testid={`barter-sig-stamp-${partyLabel.toLowerCase()}`}
      >
        {stamp || "\u00a0"}
      </p>
      {signatoryLabel ? (
        <p style={{ fontSize: 12, color: META, margin: "2px 0 0", fontWeight: 300 }}>
          {signatoryLabel}
        </p>
      ) : null}
    </div>
  );
}

/** Proposal-style document preview for barter agreements. */
export function BarterAgreementDocument({
  fields = MOCK_BARTER_AGREEMENT_FIELDS,
  partnerSignature = null,
  partnerSignedAt = null,
  studioSignature = null,
  studioSignedAt = null,
  /** @deprecated use partnerSignature */
  signatureName = null,
  /** @deprecated use partnerSignedAt */
  signedAt = null,
  clauses: clausesProp = null,
}) {
  const merged = useMemo(() => normalizeBarterFields(fields), [fields]);
  const clauses = useMemo(
    () => (Array.isArray(clausesProp) && clausesProp.length ? clausesProp : barterClausesFromFields(merged)),
    [clausesProp, merged],
  );
  const partnerImg =
    partnerSignature ||
    (signatureName && isSignatureDataUrl(signatureName) ? signatureName : null);
  const partnerStamp = partnerSignedAt || signedAt;

  return (
    <article
      className="overflow-hidden rounded-2xl border border-[var(--t-border)] bg-white"
      style={{ background: BG, color: TEXT, fontFamily: FONT }}
      data-testid="barter-agreement-document"
    >
      <div style={{ padding: "28px 24px 32px" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            borderTop: `0.5px solid ${RULE}`,
            paddingTop: 10,
            marginBottom: 26,
          }}
        >
          <span style={{ fontSize: 11, letterSpacing: "0.08em", fontWeight: 300 }}>TAĪSTU</span>
          <div style={{ textAlign: "right", fontSize: 11, lineHeight: 1.7, fontWeight: 300 }}>
            {merged.PARTNER || "—"}
            <br />
            {formatBarterStartDate(merged.START_DATE) || "—"}
          </div>
        </div>

        <h1
          style={{
            fontSize: "1.125rem",
            fontWeight: 400,
            letterSpacing: "-0.01em",
            margin: "0 0 10px",
            lineHeight: 1.3,
          }}
        >
          In-Kind Partnership Agreement
        </h1>
        <div
          style={{
            height: 0,
            borderBottom: `0.5px solid ${RULE}`,
            margin: "0 -24px 16px",
          }}
          aria-hidden
        />
        <p style={{ fontSize: 13, fontWeight: 300, lineHeight: 1.6, margin: "0 0 22px" }}>
          Between TAĪSTU and {merged.PARTNER || "—"}, this agreement establishes an in-kind
          partnership — an exchange of services of equal value, guided by these terms, with a
          fresh monthly scope defining each month&apos;s focus.
        </p>

        {clauses.map((clause, idx) => (
          <div
            key={clause.title}
            style={{
              padding: "12px 0",
              borderBottom: idx === clauses.length - 1 ? "none" : `0.3px solid ${RULE}`,
            }}
            data-testid={`barter-clause-${clause.title.toLowerCase().replace(/\s+/g, "-")}`}
          >
            <div style={{ fontSize: 13, color: ACCENT, fontWeight: 300, marginBottom: 4 }}>
              {clause.title}
            </div>
            <ClauseBody body={clause.body} />
          </div>
        ))}

        <div style={{ marginTop: 28, paddingTop: 8 }}>
          <div
            style={{
              fontSize: 10,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              color: ACCENT,
              marginBottom: 16,
            }}
          >
            Signatures
          </div>
          <div className="flex flex-col gap-8 sm:flex-row sm:gap-10">
            <SignatureSlot
              partyLabel="Partner"
              signatoryLabel={merged.FOUNDER || merged.PARTNER || "Partner"}
              image={partnerImg}
              signedAt={partnerStamp}
              empty={!partnerImg}
            />
            <SignatureSlot
              partyLabel="TAĪSTU"
              signatoryLabel="Taī"
              image={studioSignature}
              signedAt={studioSignedAt}
              empty={!studioSignature}
            />
          </div>
        </div>
      </div>
    </article>
  );
}

/** Finger-draw signature box — reserved timestamp row (no helper copy). */
export function BarterAgreementSignBox({
  label = "Your signature",
  partyLabel = "Partner",
  busy = false,
  onSign,
  disabled = false,
  submitLabel = "Sign agreement",
  timestamp = null,
}) {
  const [stroke, setStroke] = useState(null);
  const ready = Boolean(stroke);

  return (
    <section className="mt-8 space-y-4" data-testid={`barter-agreement-sign-${partyLabel.toLowerCase()}`}>
      <div className="label-eyebrow">{label}</div>
      <SignaturePad
        value={stroke}
        onChange={setStroke}
        disabled={busy || disabled}
        label="Sign with your finger or cursor"
        timestamp={timestamp}
        data-testid={`barter-signature-pad-${partyLabel.toLowerCase()}`}
      />
      <div className="flex justify-center md:justify-start">
        <button
          type="button"
          disabled={busy || !ready || disabled}
          onClick={() => onSign?.(stroke)}
          className="neo-cta disabled:opacity-50"
          data-testid={`barter-sign-submit-${partyLabel.toLowerCase()}`}
        >
          {busy ? "Signing…" : submitLabel}
        </button>
      </div>
    </section>
  );
}

export default function BarterAgreementPreviewShell({
  title = "Agreement",
  fields = MOCK_BARTER_AGREEMENT_FIELDS,
  canSignPartner = false,
  canSignStudio = false,
  onPartnerSigned,
  onStudioSigned,
  studioToggle = null,
  initialPartnerSigned = false,
  initialStudioSigned = false,
}) {
  const [partnerSig, setPartnerSig] = useState(null);
  const [studioSig, setStudioSig] = useState(null);
  const [partnerAt, setPartnerAt] = useState(initialPartnerSigned ? new Date().toISOString() : null);
  const [studioAt, setStudioAt] = useState(initialStudioSigned ? new Date().toISOString() : null);
  const merged = normalizeBarterFields(fields);

  return (
    <div className="min-h-screen bg-[var(--t-bg)]" data-testid="barter-agreement-page">
      <div className="mx-auto max-w-3xl px-4 py-10 sm:px-6 md:py-14">
        {studioToggle ? (
          <div className="mb-6 flex flex-wrap items-center justify-end gap-3">{studioToggle}</div>
        ) : null}
        <div className="label-eyebrow mb-2">TAĪSTU</div>
        <h1 className="font-heading text-3xl tracking-tight !font-extralight md:text-4xl">{title}</h1>
        <p className="mt-2 text-sm text-[var(--t-meta)]">{merged.PARTNER}</p>

        <div className="mt-8">
          <BarterAgreementDocument
            fields={merged}
            partnerSignature={partnerSig}
            partnerSignedAt={partnerAt}
            studioSignature={studioSig}
            studioSignedAt={studioAt}
          />
        </div>

        {canSignPartner && !partnerAt ? (
          <BarterAgreementSignBox
            label={`${merged.FOUNDER || "Partner"} · signature`}
            partyLabel="Partner"
            onSign={(dataUrl) => {
              const stamp = new Date().toISOString();
              setPartnerSig(dataUrl);
              setPartnerAt(stamp);
              onPartnerSigned?.({ signature: dataUrl, signedAt: stamp });
            }}
          />
        ) : null}

        {canSignStudio && partnerAt && !studioAt ? (
          <BarterAgreementSignBox
            label="TAĪSTU · Taī"
            partyLabel="Studio"
            submitLabel="Sign & send copy"
            onSign={(dataUrl) => {
              const stamp = new Date().toISOString();
              setStudioSig(dataUrl);
              setStudioAt(stamp);
              onStudioSigned?.({ signature: dataUrl, signedAt: stamp });
            }}
          />
        ) : null}

        <p className="mt-12 text-center text-[11px] tracking-[0.08em] text-[var(--t-meta)]">
          <a href="/privacy" className="hover:text-[var(--t-accent)]">
            Privacy Policy
          </a>
        </p>
      </div>
    </div>
  );
}
